// Per-page copy for the Help panel (see components/HelpPanel.js).
// Keyed by the exact route `path` used in App.js's `routes` array.
// Pages not listed here fall back to `defaultHelpContent`.

export const helpContent = {
  "/dashboard": {
    title: "Call Details",
    description:
      "The detail view for a single call recording, opened by clicking through from Campaign Home, Search, Assigned to Me, or My Calls. Split into three tabs: Overview, Transcript, and QA Assessment.",
    actions: [
      "Overview: AI-generated summary, sentiment, and loudness for the call",
      "Transcript: full text alongside synced audio playback",
      "QA Assessment: complete or review the scorecard for this call",
      "Edit call metadata or mark the call as assessed"
    ]
  },
  "/": {
    title: "Campaign Home",
    description:
      "The landing page for Admins and Super Admins. Shows every call recording that has been processed, with filters for campaign, agent, and sentiment rating.",
    actions: [
      "Filter calls by campaign, agent, or rating",
      "Assign a call to a QA agent for scoring",
      "Open a call to view its full transcript and analytics"
    ]
  },
  "/search": {
    title: "Search",
    description:
      "A multi-layered search across every processed call. Quick, Basic, and Advanced modes let you narrow results by campaign, scorecard, agent, assigned reviewer, or upload job.",
    actions: [
      "Use Quick Search for common shortcuts",
      "Switch to Advanced for combined filters",
      "Open a result to jump straight to its transcript"
    ]
  },
  "/uploadRecs": {
    title: "Upload Recordings",
    description:
      "Upload call recordings for processing. Choose the campaign and scorecard they belong to, then add files directly or pull them in via the Genesys integration.",
    actions: [
      "Select a campaign and scorecard before uploading",
      "Drag and drop audio files, or connect via Genesys",
      "Uploading 5+ files together triggers batch processing automatically"
    ]
  },
  "/jobs": {
    title: "Jobs",
    description:
      "Tracks every batch upload and processing job started from Upload Recordings, along with its status and progress.",
    actions: [
      "Check whether a batch is still processing or has finished",
      "Open a job to see per-file results on its Job Detail page"
    ]
  },
  "/templates": {
    title: "Scorecards",
    description:
      "Manage the scorecard templates QA agents use to grade calls — the questions and criteria applied during review.",
    actions: [
      "Create a new scorecard or upload one from a file",
      "Edit an existing scorecard's questions and versioning",
      "Remove scorecards that are no longer used"
    ]
  },
  "/jobs/detail": {
    title: "Job Detail",
    description:
      "The detail view for a single batch upload job, opened from the Jobs list. Shows upload metadata, processing statistics, any in-progress or Genesys ingestion status, and every call that's part of this job.",
    actions: [
      "Track how many calls are completed, processing, or failed",
      "Stop or resume an in-progress Genesys ingestion",
      "Assign this job's calls to a QA agent",
      "Export the job's calls as CSV or JSON"
    ]
  },
  "/templates/edit": {
    title: "Edit Scorecard",
    description:
      "The editor for a single scorecard, opened from the Scorecards list. Build the set of questions QA agents are asked when scoring a call against it.",
    actions: [
      "Add, edit, or remove questions, and group them into sections",
      "Choose a scoring mode and set question weights",
      "Save changes as a new version without overwriting the original"
    ]
  },
  "/campaigns": {
    title: "Campaigns",
    description:
      "Manage campaigns — the containers that group recordings together with a shared scorecard and configuration.",
    actions: [
      "Create a new campaign and assign it a scorecard",
      "Edit an existing campaign's settings",
      "Open a campaign to see its dashboard and detail view"
    ]
  },
  "/campaigns/edit": {
    title: "Edit Campaign",
    description:
      "The editor for a single campaign, opened from the Campaigns list. Set the campaign's name and which scorecards apply to it, and upload knowledge-base example files.",
    actions: [
      "Assign or remove scorecards for this campaign",
      "Upload example files to the campaign's knowledge base",
      "Create a new scorecard inline without leaving the editor"
    ]
  },
  "/scheduled-jobs": {
    title: "Scheduled Jobs",
    description:
      "Set up recurring jobs that automatically pull in and process new call recordings on a schedule, instead of uploading manually every time.",
    actions: [
      "Pick a campaign, scorecard, and recurrence (day/hour/minute)",
      "Set how far back a run should look for new recordings"
    ]
  },
  "/transcribe-quota": {
    title: "Transcribe Quota",
    description:
      "Super Admin only. Monitor Amazon Transcribe concurrency usage across all processing jobs, and see which executions are queued or throttled.",
    actions: [
      "Expand a job to see its individual executions",
      "Raise or lower the default concurrency limit"
    ]
  },
  "/override-logs": {
    title: "Audit",
    description:
      "A searchable audit trail of manual overrides and edits made to calls — metadata changes, rescoring, or corrections — with who made each change and when.",
    actions: [
      "Search by filename to find edits to a specific recording",
      "Filter by a date/time range to investigate a period"
    ]
  },
  "/users": {
    title: "User Management",
    description:
      "Super Admin only. Manage user accounts and access — who can log in, and what role (Super Admin, Admin, QA Agent, Call Agent) they have.",
    actions: [
      "Create a new user and assign them a role",
      "Reset a user's password or edit their details",
      "Search or filter the user list by group"
    ]
  },
  "/quicksight": {
    title: "Dashboard",
    description:
      "An embedded Amazon QuickSight analytics dashboard giving a cross-campaign view of call volumes, sentiment trends, and scoring outcomes over time.",
    actions: [
      "Use this for trends across many calls",
      "Use Campaign Home or Search for a single call's detail"
    ]
  },
  "/fileCopy": {
    title: "Cloud Connector",
    description:
      "Connect to an external storage source (e.g. a bucket in another AWS account), browse the recordings sitting there, and copy the ones you select into this solution.",
    actions: [
      "Connect to the source and list its contents",
      "Select the files you want and copy them in for processing"
    ]
  },
  "/assigned": {
    title: "Assigned to Me",
    description:
      "For QA Agents. Lists the calls that have been assigned to you specifically for review, filterable by campaign.",
    actions: [
      "Open a call from here to complete its scorecard"
    ]
  },
  "/CallAgent": {
    title: "My Calls",
    description:
      "For Call Agents. Lists your own calls that have gone through QA, filterable by whether they've been assessed yet.",
    actions: [
      "Open a call to see its feedback and score"
    ]
  }
};

export const defaultHelpContent = {
  title: "Help",
  description:
    "This page doesn't have dedicated help yet. Pick a section from the sidebar — the panel updates automatically with an explanation of what that page is for.",
  actions: []
};

export function getHelpForPath(pathname) {
  if (helpContent[pathname]) {
    return helpContent[pathname];
  }
  // "/dashboard/:key*" (Call Details) is a dynamic route, so it needs a prefix
  // match rather than an exact one — except its "parsedFiles/search" alias,
  // which is actually the Search page, not a call.
  if (pathname === "/dashboard/parsedFiles/search") {
    return helpContent["/search"];
  }
  if (pathname.startsWith("/dashboard/")) {
    return helpContent["/dashboard"];
  }
  // "/jobs/:jobId" (Job Detail) is likewise dynamic — the segment is the job's
  // own ID, so it can't be an exact key. "/jobs" itself (no trailing segment)
  // is the Jobs list and is already handled by the exact match above.
  if (pathname.startsWith("/jobs/")) {
    return helpContent["/jobs/detail"];
  }
  // "/templates/:name" (Edit Scorecard) is likewise dynamic — its name segment
  // is the scorecard's own name, so it can't be an exact key. "new" and
  // "upload" are separate, statically-routed pages, not an existing scorecard.
  if (
    pathname.startsWith("/templates/") &&
    pathname !== "/templates/new" &&
    pathname !== "/templates/upload"
  ) {
    return helpContent["/templates/edit"];
  }
  // "/campaigns/:name" (Edit Campaign) is likewise dynamic — its name segment
  // is the campaign's own name, so it can't be an exact key. "/campaigns/new"
  // (creating one, not editing an existing one) and "/campaigns/view/:name"
  // (Campaign Detail, a separate read-only page) are excluded.
  if (
    pathname.startsWith("/campaigns/") &&
    pathname !== "/campaigns/new" &&
    !pathname.startsWith("/campaigns/view/")
  ) {
    return helpContent["/campaigns/edit"];
  }
  return defaultHelpContent;
}
